import React, { memo, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  SwipeableDrawer,
  TextField,
  Typography,
} from '@mui/material';
import { noop } from 'utils/noop';

export interface UserSelectionItem {
  id: string;
  title: string;
  subtitle?: string;
}

interface UserRowProps {
  item: UserSelectionItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const colors = [
  '#f59e0b',
  '#3b82f6',
  '#10b981',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
];

const getAvatarColor = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 2147483647;
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (value: string) =>
  value
    .split(/[._\s-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const UserRow = memo(({ item, isSelected, onSelect }: UserRowProps) => {
  const primary = (
    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{item.title}</Typography>
  );

  return (
    <ListItemButton
      onClick={() => onSelect(item.id)}
      selected={isSelected}
      sx={{
        py: 1.5,
        px: 1,
        borderRadius: 2,
        '&.Mui-selected': { bgcolor: '#fffbeb' },
        '&.Mui-selected:hover, &:active': { bgcolor: '#fef3c7' },
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: getAvatarColor(item.title),
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {getInitials(item.title)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={primary}
        secondary={
          item.subtitle ? (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                mt: 0.25,
                bgcolor: '#fef3c7',
                color: '#92400e',
                borderRadius: 1,
                px: 1,
                py: 0.25,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {item.subtitle}
            </Box>
          ) : undefined
        }
      />
      {isSelected && <CheckCircleIcon sx={{ color: '#f59e0b', ml: 1 }} />}
    </ListItemButton>
  );
});

interface UserSelectionDrawerProps {
  open: boolean;
  items: UserSelectionItem[];
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  title?: string;
  searchPlaceholder?: string;
  emptyText?: string;
}

export const UserSelectionDrawer = ({
  open,
  items,
  isLoading,
  selectedId,
  onSelect,
  onClose,
  title = 'Выбери пользователя',
  searchPlaceholder = 'Поиск',
  emptyText = 'Ничего не найдено',
}: UserSelectionDrawerProps) => {
  const [search, setSearch] = useState('');
  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter(
      ({ title: itemTitle, subtitle }) =>
        itemTitle.toLowerCase().includes(query) ||
        subtitle?.toLowerCase().includes(query),
    );
  }, [items, search]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      onOpen={noop}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          height: '80vh',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GroupsIcon sx={{ color: '#f59e0b' }} />
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            {title}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f59e0b',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            {filteredItems.length === 0 ? (
              <Typography sx={{ color: '#9ca3af', textAlign: 'center', py: 4 }}>
                {emptyText}
              </Typography>
            ) : (
              <List disablePadding>
                {filteredItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <UserRow
                      item={item}
                      isSelected={item.id === selectedId}
                      onSelect={onSelect}
                    />
                    {index < filteredItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </>
        )}
      </Box>
    </SwipeableDrawer>
  );
};
